import invariant from 'invariant';
import lowerFirst from 'lodash/lowerFirst';
import upperFirst from 'lodash/upperFirst';
import camelCase from 'lodash/camelCase';

import round10 from './round10';
import { currencySymbolFromCode } from './currencies';
import capitalize from 'lodash/capitalize';

/**
 * A placeholder to eventually return the user's locale. For now everyone will
 * have en-US until we have good cross-browser way of detecting this.
 */
export function userLocale(): 'en-US' {
  return 'en-US';
}

export function getDashboardUrl(path: string): string {
  return `https://dashboard.watershedclimate.com${path}`;
}

type WhenPlural = string | ((singular: string) => string);

/**
 * @deprecated Use internationalization-friendly strings. Ask for help in
 * #proj-localization-eng if needed.
 *
 * returns the first argument when count is singular, otherwise returns the
 * second if it's a string or the result of calling the second argument as a
 * function with the singular argument. Mostly useful as a helper to `pluralize`
 * below, but sometimes useful for plural related prefixes such as
 * @example
 * ```ts
 *  whenPlural(countOfpeople, 'this', 'these') + ' ' + pluralizeString(countOfPeople, 'person') // => 'these people' etc
 * ```
 */
export function whenPlural(
  count: number,
  singular: string,
  plural: WhenPlural
): string {
  return count === 1
    ? singular
    : typeof plural === 'function'
      ? plural(singular)
      : plural;
}

/**
 * @deprecated Use internationalization-friendly strings. Ask for help in
 * #proj-localization-eng if needed.
 *
 * Given a singular word and the count of that word, returns either the singular
 * or plural version of the word with the count.
 *
 * e.g.: pluralize(3, 'dish') => '3 dishes'
 *
 * @param count
 * @param item -> singular version of word
 * @param options
 * @returns
 */
export function pluralize(
  count: number,
  item: string,
  options: { omitCount?: boolean } = {}
): string {
  const { omitCount = false } = options;
  const str = pluralizeString(count, item);
  if (omitCount) return str;
  return `${numberWithCommas(count)} ${str}`;
}

/**
 * See https://www.grammarly.com/blog/plural-nouns/. Ideally we'd use an
 * existing library for this, but we haven't so far so someone will need to add
 * one :')
 */
function pluralizeSpecialCaseForYApplies(item: string): boolean {
  if (!item.endsWith('y')) return false;
  if ('aeiou'.includes(item[item.length - 2])) return false;
  return true;
}

/**
 * @deprecated Use internationalization-friendly strings. Ask for help in
 * #proj-localization-eng if needed.
 */
export function makeStringPlural(item: string): string {
  if (pluralizeSpecialCaseForYApplies(item)) {
    return `${item.slice(0, -1)}ies`;
  }
  if (item.endsWith('s')) {
    return `${item}es`;
  }
  return `${item}s`;
}

/**
 * @deprecated Use internationalization-friendly strings. Ask for help in
 * #proj-localization-eng if needed.
 *
 * Given a word, returns either the singular or plural version of the word given
 * the count
 *
 * e.g., pluralizeString(1, 'dish') => 'dish' pluralizeString(2, 'dish') =>
 * 'dishes'
 */
export function pluralizeString(count: number, item: string): string {
  return whenPlural(count, item, makeStringPlural);
}

/**
 * @deprecated Use Intl.listFormat
 */
export function listFormat(
  items: Array<string>,
  conjunction: 'or' | 'and' = 'and'
): string {
  if (items.length < 2) {
    return items.join('');
  }

  const firsts = items.slice(0, items.length - 1);
  const last = items[items.length - 1];
  const addOxfordComma = items.length > 2;
  return (
    firsts.join(', ') + `${addOxfordComma ? ',' : ''} ${conjunction} ` + last
  );
}

/**
 * @deprecated Use Intl.listFormat
 */
export function listFormatWithoutConjunction(items: Array<string>): string {
  return items.join(', ').trim();
}

/**
 * Renders a list of strings as a bullet list, where each item is a line
 * starting with "- ".
 */
export function bulletList(items: Array<string>): string {
  return items.map((item) => `- ${item}`).join('\n');
}

/**
 * Format a percentage as a displayable number.
 * @param pct A pct number (0 => 100 or more).
 */
export function formatPercentage(pct: number): string {
  if (pct % 1 === 0) {
    return numberWithCommas(pct, 0);
  }
  const fractionDigits = pct > 10 ? 1 : 2;
  return numberWithCommas(pct, fractionDigits);
}

export function formatPercentageSmart(
  fraction: number,
  options: {
    fixedRoundingPlaces?: number;
    includePositiveSign?: boolean;
    includeCommaSeparators?: boolean;
  } = {}
): string {
  const { fixedRoundingPlaces, includePositiveSign, includeCommaSeparators } =
    options;

  const percent = fraction * 100;

  if (percent === Infinity) {
    return '∞';
  }
  const isNegative = percent < 0;
  const absolutePercent = Math.abs(percent);
  const potentialNegativeSign = isNegative
    ? '-'
    : includePositiveSign
      ? '+'
      : '';

  if (absolutePercent === 0) {
    if (fixedRoundingPlaces !== undefined) {
      return `${(0).toFixed(fixedRoundingPlaces)}%`;
    }
    return '0%';
  }
  // short-circuit if component knows number of decimal places
  if (fixedRoundingPlaces !== undefined) {
    if (parseFloat(absolutePercent.toFixed(fixedRoundingPlaces)) === 0) {
      const formattedDecimalPercentage = formatDecimalNumberNonzero(
        percent,
        fixedRoundingPlaces,
        fixedRoundingPlaces
      );
      return `${formattedDecimalPercentage}%`;
    } else {
      return `${potentialNegativeSign}${absolutePercent.toFixed(
        fixedRoundingPlaces
      )}%`;
    }
  }

  if (absolutePercent < 0.1) {
    const inequality = isNegative ? '>' : '<';
    return `${inequality}${potentialNegativeSign}0.1%`;
  }

  // For percents without decimals, return them without adding extra decimals.
  // E.g. 6 -> 6% instead of 6 -> 6.0%
  const roundingPlaces = absolutePercent < 10 ? 1 : 0;
  const roundedPercent = parseFloat(absolutePercent.toFixed(roundingPlaces));
  if (roundedPercent % 1 === 0) {
    return `${potentialNegativeSign}${
      includeCommaSeparators ? numberWithCommas(roundedPercent) : roundedPercent
    }%`;
  }

  return `${potentialNegativeSign}${absolutePercent.toFixed(roundingPlaces)}%`;
}

// Rounds the give number to fractionDigits decimal points of precision.
//
// By default numbers less than 10 are given one point of precision and greater
// numbers are rounded to the nearest whole.
export function roundNumber(
  num: number,
  fractionDigits = Math.abs(num) > 10 ? 0 : 1
): number {
  return round10(num, fractionDigits);
}

// Formats a number with commas and rounds it to fractionDigits decimal points
// If `num` is less than `min`, it will be formatted as `< min` This is useful
// when you don't want really small numbers to be displayed as 0
export function formatPositiveNumberWithMin(
  num: number,
  min: number,
  fractionDigits?: number
): string {
  if (num < min) {
    return `< ${formatNumber(min, fractionDigits)}`;
  }
  return formatNumber(num, fractionDigits);
}

export function formatNumber(
  num: number,
  fractionDigits = Math.abs(num) > 10 ? 0 : 1,
  {
    includePointZero,
    disableCommas,
  }: { includePointZero?: boolean; disableCommas?: boolean } = {}
): string {
  const numWithCommas = numberWithCommas(num, fractionDigits, disableCommas);

  if (!includePointZero && numWithCommas.includes('.')) {
    // Trim any trailing zeros and also the '.' if that's all that was left.
    const withoutTrailingZeros = numWithCommas.replace(/0+$/, '');
    if (withoutTrailingZeros.indexOf('.') === withoutTrailingZeros.length - 1) {
      return withoutTrailingZeros.slice(0, withoutTrailingZeros.length - 1);
    } else {
      return withoutTrailingZeros;
    }
  }
  return numWithCommas;
}

// Finds the first decimal place that makes the value nonzero. For example, if
// you are given 0.00004555 and min is 1 and max is 8, this function will find
// 0.00005 as the first non-zero rounded number.
export function formatDecimalNumberNonzero(
  num: number,
  minDecimalPlaces: number,
  maxDecimalPlaces: number
): string {
  invariant(
    minDecimalPlaces <= maxDecimalPlaces,
    'minDecimalPlaces must be less than or equal to maxDecimalPlaces'
  );
  invariant(
    minDecimalPlaces >= 0 && maxDecimalPlaces >= 0,
    'both params have to be greater than or equal to 0'
  );
  if (num === 0) {
    return formatNumber(num, minDecimalPlaces, { includePointZero: true });
  }
  // find the first dec place that makes the rounded number non-zero
  for (
    let currDigits = minDecimalPlaces;
    currDigits <= maxDecimalPlaces;
    currDigits++
  ) {
    const curr = formatNumber(num, currDigits);
    if (parseFloat(curr) !== 0) {
      return curr;
    }
  }
  // the number is non-zero, but we can't format it to a non-zero number with
  // `maxDecimalPlaces`, the maximum number of fractional digits allowed
  const signAndInequality = num < 0 ? '>-' : '<';
  const zeroes = new Array(Math.max(minDecimalPlaces - 1, 0)).fill(0).join('');
  return `${signAndInequality}0.${zeroes}1`;
}

export function formatWithSign(value: number): string {
  if (value >= 0) {
    return `+${formatNumber(value)}`;
  }
  return formatNumber(value);
}

// Converts a number to its ordinal (aka rank) string, e.g. 1 -> 1st, 2 -> 2nd,
// 3 -> 3rd, 11 -> 11th, 101 -> 101st
export function formatOrdinal(value: number): string {
  const ordinalRules = new Intl.PluralRules('en', { type: 'ordinal' });
  const suffixes: Record<Intl.LDMLPluralRule, string> = {
    zero: 'th',
    one: 'st',
    two: 'nd',
    few: 'rd',
    many: 'th',
    other: 'th',
  };
  const category = ordinalRules.select(value);
  const suffix = suffixes[category];
  return `${value}${suffix}`;
}

function numberWithCommas(
  x: number,
  fractionDigits?: number,
  disableCommas?: boolean
): string {
  if (fractionDigits === undefined) {
    // To preserve earlier behavior, if no `fractionDigits` arg supplied, use
    // whatever the number intrinsically has.
    fractionDigits = (x.toString().split('.')[1] || '').length;
  }

  // In the common case where we're rounding to whole numbers (e.g., on the
  // footprint drilldown page) this speeds things up by a factor of 30 (!)
  if (fractionDigits === 0 && !disableCommas) {
    return Math.round(x).toLocaleString('en-US');
  }

  // Intl.NumberFormat min/max fraction digits must be between 0 and 20.
  fractionDigits = Math.max(0, Math.min(20, fractionDigits));

  return x.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    // fall back to undefined because this option is not a boolean it has true,
    // false and a bunch of other string options, and those are choosen
    // dynamically as a default, so let the browser do it's thing with
    // `undefined` see:
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#usegrouping
    useGrouping: disableCommas ? false : undefined,
  });
}

export function formatFileSize(bytes: number): string {
  if (bytes > 1024 ** 3) {
    return `${formatNumber(bytes / 1024 ** 3)} GB`;
  } else if (bytes > 1024 ** 2) {
    return `${formatNumber(bytes / 1024 ** 2)} MB`;
  } else {
    return `${formatNumber(bytes / 1024)} KB`;
  }
}

export function formatNumberMagnitude(
  num: number,
  fractionDigitsForSmallNumber: number = 2
): string {
  if (Math.abs(num) >= 1_000_000_000) {
    return `${formatNumber(num / 1_000_000_000)}B`;
  }
  if (Math.abs(num) >= 1_000_000) {
    return `${formatNumber(num / 1_000_000)}M`;
  }
  if (Math.abs(num) >= 1_000) {
    return `${formatNumber(num / 1_000)}K`;
  }
  if (Math.abs(num) >= 1) {
    return formatNumber(num);
  }
  // For small numbers show decimals
  return `${formatNumber(num, fractionDigitsForSmallNumber)}`;
}

export function formatCost(
  cost: number | null | undefined,
  { withCents = false, withMagnitude = true, currencyCode = 'USD' } = {}
): string {
  const symbol = currencySymbolFromCode(currencyCode);
  if (cost == null || Number.isNaN(cost)) {
    return '—';
  }
  // Skip magnitude formatting
  if (withCents) {
    return `${symbol}${numberWithCommas(cost, 2)}`;
  }

  return withMagnitude
    ? `${symbol}${formatNumberMagnitude(cost)}`
    : `${symbol}${numberWithCommas(cost, 0)}`;
}

export function formatUsdCents(usdCents: number): string {
  return formatCost(usdCents / 100, {
    withCents: usdCents % 100 !== 0,
    withMagnitude: false,
  });
}

export function formatUsdCentsPriceRange(
  priceUsdCentsMin: number,
  priceUsdCentsMax: number
): string {
  return priceUsdCentsMin === priceUsdCentsMax
    ? formatUsdCents(priceUsdCentsMin)
    : `${formatUsdCents(priceUsdCentsMin)} – ${formatUsdCents(
        priceUsdCentsMax
      )}`;
}

export function numberWithSigFigs(x: number, sigFigs: number): string {
  return x.toLocaleString('en-US', {
    minimumSignificantDigits: sigFigs,
    maximumSignificantDigits: sigFigs,
  });
}

function randomString(): string {
  return Math.random().toString(36).substring(2, 15);
}

function randomDigits(length: number): string {
  let out = '';
  for (let i = 0; i < length; i++) {
    out += Math.floor(Math.random() * 10).toString();
  }
  return out;
}

function subscriptDigit(digit: number): string {
  return String.fromCharCode('\u2080'.charCodeAt(0) + digit);
}

function subscriptDigits(input: string): string {
  const output: Array<string> = [];
  for (let i = 0; i < input.length; i++) {
    const char = input.charAt(i);
    if (char >= '0' && char <= '9') {
      output.push(subscriptDigit(parseInt(char, 10)));
    } else {
      output.push(char);
    }
  }
  return output.join('');
}

// Splits a camelCaseString or a TitleCaseString into multiple sentence-cased
// words.
export function splitByCaps(input: string): string {
  return smartSentenceCase(input.replace(/([A-Z])/g, ' $1').trim());
}

export function uppercaseIdStr(str: string): string {
  return str.replace(/\bid\b/, 'ID');
}

export function startCase(input: string): string {
  return input
    .split(/\s+/)
    .map((part) => {
      // If acronym, skip
      if (part.toUpperCase() === part) {
        return part;
      } else {
        return upperFirst(part);
      }
    })
    .join(' ');
}

export function underscoresToSpaces(input: string): string {
  return input.split('_').join(' ');
}

export function smartLowerFirst(input: string): string {
  return input
    .split(/\s+/)
    .map((part) => {
      if (part.toUpperCase() === part) {
        return part;
      } else {
        return lowerFirst(part);
      }
    })
    .join(' ');
}

export function smartLowerCase(input: string): string {
  return input
    .split(/\s+/)
    .map((part) => {
      if (part.toUpperCase() === part) {
        return part;
      } else {
        return part.toLocaleLowerCase();
      }
    })
    .join(' ');
}

export function smartSentenceCase(input: string): string {
  const sentenceCased = input
    .split(/\s+/)
    .map((part, i) => {
      if (part.toUpperCase() === part) {
        return part;
      } else {
        if (i > 0) {
          return part.toLocaleLowerCase();
        } else {
          return upperFirst(part);
        }
      }
    })
    .join(' ');
  return uppercaseIdStr(sentenceCased);
}

export function humanize(input: string): string {
  return splitByCaps(underscoresToSpaces(input));
}

/**
 * Converts a string to Title Case.
 */
export function titleCase(
  input: string,
  { allCapsMaxLetters = 3 }: { allCapsMaxLetters?: number } = {}
): string {
  return input
    .split(/\s+/)
    .map((part, i) => {
      if (
        allCapsMaxLetters &&
        part.toUpperCase() === part &&
        part.length <= allCapsMaxLetters
      ) {
        return part;
      }
      let result = '';
      for (let i = 0; i < part.length; i++) {
        if (i === 0) {
          result += part[i].toLocaleUpperCase();
        } else if (part[i - 1] === '.' && part[i + 1] === '.') {
          result += part[i].toLocaleUpperCase();
        } else {
          result += part[i].toLocaleLowerCase();
        }
      }
      return result;
    })
    .join(' ');
}

const vowels = new Set(['a', 'e', 'i', 'o', 'u']);
/**
 * Returns a string with the specified noun preceded by the appropriate
 * indefinite article (a/an).
 */
export function withIndefiniteArticle(noun: string): string {
  return vowels.has(noun.toLowerCase()[0]) ? `an ${noun}` : `a ${noun}`;
}

export function smartSentenceCaseSpaced(input: string): string {
  return smartSentenceCase(underscoresToSpaces(input));
}

export function dumbSentenceCaseSpaced(input: string): string {
  return capitalize(underscoresToSpaces(input));
}

export function temporalWebUrl(path: string): string {
  return new URL(
    path,
    process.env.NEXT_PUBLIC_TEMPORAL_WEB_ADDRESS ?? 'http://localhost:8088'
  ).toString();
}

export function apiUrl(path: string): string {
  return new URL(path, 'https://api.watershedclimate.com').toString();
}

export function apiDocsUrl(path: string): string {
  return new URL(path, 'https://api-docs.watershedclimate.com').toString();
}

/**
 * Return an environment specific dashboard URL. Fallback to localhost for
 * Storybook. E.g. localhost:3000 vs dashboard.watershedclimate.com
 * @param path
 */
export function dashboardUrl(path: string): string {
  return new URL(
    path,
    process.env.NEXT_PUBLIC_DASHBOARD_HOST ?? 'http://localhost:3000'
  ).toString();
}

export type UtmParams = {
  utm_source?: string;
  utm_medium?: string;
};

export function addUtmParams(path: string, params: UtmParams): string {
  const url = new URL(path);
  if (params.utm_source) {
    url.searchParams.set('utm_source', params.utm_source);
  }
  if (params.utm_medium) {
    url.searchParams.set('utm_medium', params.utm_medium);
  }
  return url.toString();
}

/**
 * Return an environment specific admin URL. E.g. localhost:3001 vs
 * admin.watershedclimate.com
 * @param path
 */
export function adminUrl(path: string): string {
  return new URL(
    path,
    process.env.NEXT_PUBLIC_ADMIN_HOST ?? 'http://localhost:3001'
  ).toString();
}

export function joinWithConjunction(
  inputWords: Iterable<string>,
  conjunction: string
): string {
  const words = Array.from(inputWords);
  if (words.length === 0) {
    return '';
  } else if (words.length === 1) {
    return words[0];
  } else if (words.length === 2) {
    return `${words[0]} ${conjunction} ${words[1]}`;
  } else {
    const allButLast = words.slice(0, words.length - 1);
    return `${allButLast.join(', ')}, ${conjunction} ${
      words[words.length - 1]
    }`;
  }
}

export function joinWithAnd(words: Iterable<string>): string {
  return joinWithConjunction(words, 'and');
}

export function joinQuotedWithConjunction(
  words: Iterable<string>,
  conjunction: string,
  quote = '"'
): string {
  return joinWithConjunction(
    Array.from(words).map((word) => quote + word + quote),
    conjunction
  );
}

export function joinQuotedWithAnd(words: Iterable<string>): string {
  return joinQuotedWithConjunction(words, 'and', '"');
}

export function joinTickedWithAnd(words: Iterable<string>): string {
  return joinQuotedWithConjunction(words, 'and', '`');
}

export function getRevenueIntensity(
  kgCo2e: number,
  revenueDollars: number
): number {
  const tCo2e = kgCo2e / 1000;
  return tCo2e / (revenueDollars / 1_000_000);
}

/**
 * Assumes american formatting of commas for separating thousands.
 * @param stringValue
 * @returns number
 */
export function parseFloatWithCommas(stringValue: string): number {
  return parseFloat(stringValue.replace(/,/g, ''));
}

// Normalize user-inputted strings
export function normalizeValue(inputValue: string): string {
  let result = inputValue.trim();

  if (result === '') {
    return '0';
  }

  // This is to convert cases like .4 -> 0.4, -.4 -> -0.4.
  const noPrecedingZeroForOnlyDecimalRegex = /(-?)(\..*)/;
  const parts = noPrecedingZeroForOnlyDecimalRegex.exec(result)?.groups;
  if (parts) {
    const optSignPart = parts[1];
    const decimalPart = parts[2];

    result = `${optSignPart}0${decimalPart}`;
  }

  // Remove trailing decimal point
  if (result.endsWith('.')) {
    result = result.substr(0, result.length - 1);
  }

  // Pass to-and-from number
  const valueAsNumber = parseFloat(result);
  const numberAsString = valueAsNumber.toString();
  return numberAsString;
}

// This is the same as normalizeValue, but allows for empty values
export function normalizeValueAllowEmpty(inputValue: string): string {
  let result = inputValue.trim();

  if (result === '') {
    return '';
  }

  // This is to convert cases like .4 -> 0.4, -.4 -> -0.4.
  const noPrecedingZeroForOnlyDecimalRegex = /(-?)(\..*)/;
  const parts = noPrecedingZeroForOnlyDecimalRegex.exec(result)?.groups;
  if (parts) {
    const optSignPart = parts[1];
    const decimalPart = parts[2];

    result = `${optSignPart}0${decimalPart}`;
  }

  // Remove trailing decimal point
  if (result.endsWith('.')) {
    result = result.substr(0, result.length - 1);
  }

  // Pass to-and-from number
  const valueAsNumber = parseFloat(result);
  const numberAsString = valueAsNumber.toString();
  return numberAsString;
}

export { numberWithCommas, randomString, subscriptDigits, randomDigits };

/**
 * Converts a string to PascalCase.
 */
export function pascalCase(s: string): string {
  return upperFirst(camelCase(s));
}

/**
 * Returns a boolean indicating whether a value is a Promise.
 */
export function isPromise(value: unknown): value is Promise<unknown> {
  return (
    value instanceof Promise ||
    (typeof (value as Promise<unknown> | null)?.then === 'function' &&
      typeof (value as Promise<unknown> | null)?.catch === 'function' &&
      typeof (value as Promise<unknown> | null)?.finally === 'function')
  );
}

/**
 * Returns a boolean indicating whether a Date is invalid. An "invalid Date" is
 * like what you get from `new Date('foo')`.
 */
export function isDateInvalid(input: Date): boolean {
  return isNaN(Number(input));
}

/**
 * Returns two letters representing a name. If the name has multiple words
 * (space-separated chunks), returns the first letters of the first and last
 * words. Otherwise, returns the first two letters of the name.
 */
export function nameToInitials(name: string): string {
  const words = name.toLocaleUpperCase().split(' ');
  if (words.length === 1) {
    return words[0].slice(0, 2);
  } else {
    return `${words[0][0]}${words[words.length - 1][0]}`;
  }
}
